import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';

import ChatInput from './ChatInput';
import classes from './EmptyChatOptions.module.scss';

interface EmptyChatOptionsProps {
  isLoading?: boolean;
  onSendMessage: (message: string) => void;
}

export default function EmptyChatOptions({ onSendMessage, isLoading }: EmptyChatOptionsProps) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'EmptyChatOptions');
  const { project } = useContext(ProjectContext);
  const questions = project.owlbot.exampleQuestions;
  const question1 = questions?.question1 || '';
  const question2 = questions?.question2 || '';
  const question3 = questions?.question3 || '';
  const filteredQuestions = [question1, question2, question3].filter(q => q.trim() !== '');

  const handleExampleQuestionClick = (question: string) => {
    if (!isLoading) {
      onSendMessage(question);
    }
  };

  return (
    <div className={classy(bem('&'), 'owlbot-empty-chat', 'rm-AskAi-empty')}>
      <div className={bem('-container')}>
        <div className={bem('-header')}>
          <svg aria-hidden="true" className={bem('-logo')} fill="none" height="24" viewBox="0 0 24 24" width="24">
            <path d="M13.386 0.544156C13.6474 0.544156 13.8381 0.735298 13.8882 1.00658C14.6014 6.51073 15.375 7.34473 20.8191 7.9473C21.1002 7.9773 21.2914 8.18815 21.2914 8.44958C21.2914 8.71101 21.1002 8.91158 20.8191 8.95187C15.375 9.55444 14.6014 10.388 13.8882 15.8926C13.8381 16.1639 13.6474 16.3447 13.386 16.3447C13.1245 16.3447 12.9342 16.1639 12.894 15.8926C12.1808 10.388 11.3974 9.55444 5.9631 8.95187C5.67167 8.91158 5.48096 8.71058 5.48096 8.44958C5.48096 8.18816 5.67167 7.9773 5.9631 7.9473C11.3871 7.23415 12.1405 6.50087 12.894 1.00658C12.9342 0.735298 13.125 0.544156 13.386 0.544156Z" />
            <path d="M6.91762 11.9649C6.89747 11.7841 6.76676 11.6636 6.57605 11.6636C6.39519 11.6636 6.26447 11.7841 6.24433 11.9851C5.91305 14.6671 5.79262 14.7374 3.06005 15.1792C2.83933 15.2092 2.70862 15.3198 2.70862 15.5208C2.70862 15.7016 2.83933 15.8221 3.02019 15.8624C5.77247 16.3745 5.91305 16.3646 6.24433 19.0565C6.26447 19.2472 6.39519 19.3779 6.57605 19.3779C6.76676 19.3779 6.89748 19.2472 6.91762 19.0664C7.26905 16.3342 7.36933 16.2438 10.1418 15.8624C10.3226 15.8319 10.4533 15.7016 10.4533 15.5208C10.4533 15.3301 10.3226 15.2092 10.1418 15.1792L10.0872 15.1687C7.36813 14.6468 7.27652 14.6292 6.91762 11.9649Z" />
            <path d="M11.6486 18.7555C11.6182 18.6449 11.5577 18.5746 11.4377 18.5746C11.3173 18.5746 11.2564 18.6449 11.2264 18.7555C10.9153 20.4329 10.9354 20.4531 9.1873 20.8045C9.07715 20.8246 8.99658 20.8949 8.99658 21.0153C8.99658 21.1362 9.07715 21.2065 9.18772 21.2262C10.935 21.5781 10.875 21.6179 11.2264 23.2756C11.2569 23.3858 11.3173 23.4561 11.4377 23.4561C11.5582 23.4561 11.6186 23.3858 11.6486 23.2756C12 21.6179 11.9499 21.5776 13.6873 21.2262C13.8082 21.2061 13.8784 21.1358 13.8784 21.0153C13.8784 20.8945 13.8077 20.8246 13.6877 20.8045C11.94 20.4732 11.9597 20.4329 11.6486 18.7555Z" />
            <linearGradient id="gradient" x1="0%" x2="50%" y1="0%" y2="0%">
              <stop offset="0%" />
              <stop offset="100%" />
            </linearGradient>
          </svg>

          <h3 className={bem('-title')}>{t('owlbotChat.emptyTitle')}</h3>
          <p className={bem('-subtitle')}>{t('owlbotChat.emptyMessage')}</p>
        </div>

        <div className={bem('-inputWrapper')}>
          <ChatInput isLoading={isLoading} onSendMessage={onSendMessage} variant="empty" />
          {filteredQuestions.length > 0 && (
            <div className={bem('-prompts')}>
              {filteredQuestions.map((question: string, index: number) => (
                <button
                  key={index}
                  className={bem('-prompt', isLoading && '-prompt_disabled')}
                  disabled={isLoading}
                  onClick={() => handleExampleQuestionClick(question)}
                  type="button"
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
